import { Component, Input } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { AbschlussKategorie } from '../../../models/unterlagen.model';
import { AusbildungForm, LehreFormGroup, SchuleFormGroup, StudiumFormGroup } from '../bewerbung-einreichen/bewerbung-einreichen-form-groups';
import { TauresMessageModule } from '@taures/taures-components/taures-message';
import { Button } from 'primeng/button';
import { SchuleComponent } from './schule/schule.component';
import { Message } from 'primeng/message';
import { MenuItem, PrimeTemplate } from 'primeng/api';
import { Card } from 'primeng/card';
import { Menu } from 'primeng/menu';
import { StudiumComponent } from './studium/studium.component';
import { LehreComponent } from './lehre/lehre.component';
import { AbschlussTyp } from './abschluss-typ';

export const schuleRequiredValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  if (!(control instanceof FormArray)) {
    return null;
  }

  const hasSchule: boolean = control.controls.some((c: AbstractControl): boolean => c.get('typ')?.value === AbschlussKategorie.schule);

  return hasSchule ? null : { schuleRequired: true };
};

@Component({
  selector: 'app-bildung[ausbildungen]',
  templateUrl: './bildung.component.html',
  styleUrls: ['./bildung.component.scss'],
  imports: [ReactiveFormsModule, TauresMessageModule, Button, SchuleComponent, Message, Card, Menu, PrimeTemplate, StudiumComponent, LehreComponent],
  standalone: true
})
export class BildungComponent {
  @Input()
  public ausbildungen!: FormArray<AusbildungForm>;

  protected readonly ausbildungPopupItems: MenuItem[] = [
    {
      label: 'Lehre/Berufsausbildung',
      command: (): void => {
        this.ausbildungen.push(
          new FormGroup<LehreFormGroup>({
            typ: new FormControl<AbschlussKategorie.lehre>(AbschlussKategorie.lehre, { nonNullable: true }),
            unternehmen: new FormControl<string | null>(null, { validators: [Validators.required] }),
            branche: new FormControl<string | null>(null),
            abschluss: new FormControl<string | null>(null),
            abschlussTyp: new FormControl<string>(AbschlussTyp.ABGESCHLOSSENE_BERUFSAUSBILDUNG, { nonNullable: true }),
            von: new FormControl<Date | null>(null, { validators: [Validators.required] }),
            bis: new FormControl<Date | null>(null)
          })
        );
      }
    },
    {
      label: 'Studium',
      command: (): void => {
        this.ausbildungen.push(
          new FormGroup<StudiumFormGroup>({
            typ: new FormControl<AbschlussKategorie.studium>(AbschlussKategorie.studium, { nonNullable: true }),
            hochschule: new FormControl<string | null>(null, { validators: [Validators.required] }),
            fachrichtung: new FormControl<string | null>(null),
            abschluss: new FormControl<string | null>(null),
            andererAbschluss: new FormControl<string | null>(null),
            abschlussTyp: new FormControl<string | null>(null),
            von: new FormControl<Date | null>(null, { validators: [Validators.required] }),
            bis: new FormControl<Date | null>(null)
          })
        );
      }
    }
  ];

  public constructor() {}

  protected isSchule(ausbildung: AusbildungForm): boolean {
    return ausbildung.value.typ === AbschlussKategorie.schule;
  }

  protected isStudium(ausbildung: AusbildungForm): boolean {
    return ausbildung.value.typ === AbschlussKategorie.studium;
  }

  protected isLehre(ausbildung: AusbildungForm): boolean {
    return ausbildung.value.typ === AbschlussKategorie.lehre;
  }

  protected removeAusbildung(i: number): void {
    this.ausbildungen.removeAt(i);
  }

  protected addSchule(): void {
    this.ausbildungen.push(
      new FormGroup<SchuleFormGroup>({
        typ: new FormControl<AbschlussKategorie.schule>(AbschlussKategorie.schule, { nonNullable: true }),
        name: new FormControl<string | null>(null, { validators: [Validators.required] }),
        abschluss: new FormControl<string | null>(null),
        abschlussTyp: new FormControl<string | null>(null),
        ende: new FormControl<Date | null>(null)
      })
    );
  }

  protected isSchuleForm(form: AusbildungForm): form is FormGroup<SchuleFormGroup> {
    return form.controls.typ.value === AbschlussKategorie.schule;
  }

  protected mapToSchuleFormGroup(ausbildungForm: AusbildungForm): FormGroup<SchuleFormGroup> | null {
    return this.isSchuleForm(ausbildungForm) ? ausbildungForm : null;
  }

  protected isLehreForm(form: AusbildungForm): form is FormGroup<LehreFormGroup> {
    return form.controls.typ.value === AbschlussKategorie.lehre;
  }

  protected mapToLehreFormGroup(ausbildungForm: AusbildungForm): FormGroup<LehreFormGroup> | null {
    return this.isLehreForm(ausbildungForm) ? ausbildungForm : null;
  }

  protected isStudiumForm(form: AusbildungForm): form is FormGroup<StudiumFormGroup> {
    return form.controls.typ.value === AbschlussKategorie.studium;
  }

  protected mapToStudiumFormGroup(ausbildungForm: AusbildungForm): FormGroup<StudiumFormGroup> | null {
    return this.isStudiumForm(ausbildungForm) ? ausbildungForm : null;
  }
}
